import { useMemo, useState } from "react";
import {
  BookOpen,
  CheckCircle2,
  ExternalLink,
  FileWarning,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import { blankIncident } from "../lib/projectStore";
import type {
  AssuranceStatus,
  CriticalControl,
  DesignRisk,
  IncidentRecord,
} from "../types/domain";

const LEARNING_SOURCES = [
  {
    title: "Malaysia occupational injury statistics",
    publisher: "Department of Statistics Malaysia",
    description:
      "Use national injury trends to challenge project assumptions and prioritise recurring harm mechanisms.",
    url: "https://open.dosm.gov.my/publications/osh_2023",
    prompts: ["Falls and struck-by exposure", "Sector injury trend", "Severity versus frequency"],
  },
  {
    title: "Fatality Assessment and Control Evaluation",
    publisher: "NIOSH FACE",
    description:
      "Investigation reports translate fatal incidents into prevention recommendations that can be tested against design decisions.",
    url: "https://www.cdc.gov/niosh/face/about/index.html",
    prompts: ["Design-out opportunity", "Failed or missing barrier", "Transferable lesson"],
  },
  {
    title: "OSHA establishment and incident data",
    publisher: "US Occupational Safety and Health Administration",
    description:
      "A large public data source for researching hazard patterns, enforcement themes and incident circumstances.",
    url: "https://www.osha.gov/data",
    prompts: ["Similar activity", "Control failure pattern", "Evidence to request"],
  },
  {
    title: "Prevention through Design toolkit",
    publisher: "NIOSH",
    description:
      "Structured PtD worksheets and examples for moving incident learning upstream into design alternatives.",
    url: "https://www.cdc.gov/niosh/publications/numbered/2024-124.html",
    prompts: ["Eliminate at source", "Lifecycle exposure", "Safer design alternative"],
  },
] as const;

interface IncidentsProps {
  incidents: IncidentRecord[];
  risks: DesignRisk[];
  controls: CriticalControl[];
  onSave: (incident: IncidentRecord) => void;
  onRemove: (id: string) => void;
}

export function IncidentsView({
  incidents,
  risks,
  controls,
  onSave,
  onRemove,
}: IncidentsProps) {
  const [draft, setDraft] = useState<IncidentRecord | null>(null);
  const [query, setQuery] = useState("");
  const visible = useMemo(
    () =>
      incidents.filter((item) =>
        `${item.title} ${item.description} ${item.lessons} ${item.location}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [incidents, query],
  );

  const save = () => {
    if (!draft) return;
    onSave({ ...draft, updatedAt: new Date().toISOString() });
    setDraft(null);
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker">
            <FileWarning size={16} /> Incident-to-design intelligence
          </span>
          <h2>Turn every event and external lesson into a safer design decision.</h2>
          <p>
            Record immediate and root causes, identify failed controls and connect transferable
            lessons directly to design risks and critical controls.
          </p>
        </div>
      </section>

      <section className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search incidents and lessons" />
        </div>
        <button className="button button-primary" onClick={() => setDraft(blankIncident())}>
          <Plus size={18} /> Record event or lesson
        </button>
      </section>

      {incidents.length === 0 ? (
        <EmptyState
          icon={<FileWarning size={32} />}
          title="No project learning records"
          action={
            <button className="button button-primary" onClick={() => setDraft(blankIncident())}>
              <Plus size={18} /> Add the first record
            </button>
          }
        >
          Capture project incidents, near misses and external learning. The value is created when a
          lesson is linked to a risk or control and changes what the project does.
        </EmptyState>
      ) : (
        <section className="incident-list">
          {visible.map((incident) => (
            <article className="incident-card" key={incident.id}>
              <header>
                <div>
                  <span className="eyebrow">{incident.eventType} · {incident.jurisdiction}</span>
                  <h3>{incident.title || "Untitled learning record"}</h3>
                </div>
                <StatusPill status={incident.status === "closed" || incident.status === "accepted" ? "success" : "warning"}>
                  {incident.status}
                </StatusPill>
              </header>
              <p>{incident.description || "Description not recorded."}</p>
              <div className="incident-analysis">
                <div><small>Root causes</small><p>{incident.rootCauses || "Not analysed"}</p></div>
                <div><small>Failed controls</small><p>{incident.failedControls || "Not analysed"}</p></div>
                <div><small>Design learning</small><p>{incident.lessons || "Not captured"}</p></div>
              </div>
              <footer>
                <span>{incident.linkedRiskIds.length} linked risk(s)</span>
                <span>{incident.linkedControlIds.length} linked control(s)</span>
                {incident.sourceUrl && (
                  <a href={incident.sourceUrl} target="_blank" rel="noreferrer">Source <ExternalLink size={13} /></a>
                )}
                <div className="row-actions">
                  <button className="button-link" onClick={() => setDraft(incident)}>Edit</button>
                  <button
                    className="icon-button danger"
                    aria-label="Delete incident"
                    onClick={() => window.confirm("Delete this learning record?") && onRemove(incident.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </footer>
            </article>
          ))}
        </section>
      )}

      <section className="learning-library">
        <div className="section-heading-row">
          <div>
            <span className="eyebrow">Curated research gateways</span>
            <h2>Incident learning library</h2>
          </div>
          <BookOpen size={24} />
        </div>
        <div className="learning-grid">
          {LEARNING_SOURCES.map((source) => (
            <article key={source.title}>
              <span>{source.publisher}</span>
              <h3>{source.title}</h3>
              <p>{source.description}</p>
              <ul>{source.prompts.map((prompt) => <li key={prompt}>{prompt}</li>)}</ul>
              <a href={source.url} target="_blank" rel="noreferrer">
                Explore source <ExternalLink size={14} />
              </a>
            </article>
          ))}
        </div>
      </section>

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Learning record</span>
                <h2>{incidents.some((item) => item.id === draft.id) ? "Edit" : "Add"} incident intelligence</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close"><X size={21} /></button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Title" wide>
                  <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                </Field>
                <Field label="Event type">
                  <select value={draft.eventType} onChange={(e) => setDraft({ ...draft, eventType: e.target.value as IncidentRecord["eventType"] })}>
                    <option value="near-miss">Near miss</option>
                    <option value="injury">Injury</option>
                    <option value="dangerous-occurrence">Dangerous occurrence</option>
                    <option value="property-damage">Property damage</option>
                    <option value="environmental">Environmental</option>
                    <option value="external-learning">External learning</option>
                  </select>
                </Field>
                <Field label="Severity">
                  <select value={draft.severity} onChange={(e) => setDraft({ ...draft, severity: e.target.value as IncidentRecord["severity"] })}>
                    <option value="learning">Learning only</option>
                    <option value="minor">Minor</option>
                    <option value="moderate">Moderate</option>
                    <option value="major">Major</option>
                    <option value="fatal">Fatal</option>
                  </select>
                </Field>
                <Field label="Date">
                  <input type="date" value={draft.occurredAt} onChange={(e) => setDraft({ ...draft, occurredAt: e.target.value })} />
                </Field>
                <Field label="Location / jurisdiction">
                  <input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
                </Field>
                <Field label="Source URL" wide>
                  <input type="url" value={draft.sourceUrl} onChange={(e) => setDraft({ ...draft, sourceUrl: e.target.value })} />
                </Field>
                <Field label="What happened" wide>
                  <textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                </Field>
                <Field label="Immediate causes" wide>
                  <textarea rows={3} value={draft.immediateCauses} onChange={(e) => setDraft({ ...draft, immediateCauses: e.target.value })} />
                </Field>
                <Field label="Underlying / root causes" wide>
                  <textarea rows={3} value={draft.rootCauses} onChange={(e) => setDraft({ ...draft, rootCauses: e.target.value })} />
                </Field>
                <Field label="Failed or missing controls" wide>
                  <textarea rows={3} value={draft.failedControls} onChange={(e) => setDraft({ ...draft, failedControls: e.target.value })} />
                </Field>
                <Field label="Transferable design and planning lessons" wide>
                  <textarea rows={3} value={draft.lessons} onChange={(e) => setDraft({ ...draft, lessons: e.target.value })} />
                </Field>
                <Field label="Owner">
                  <input value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} />
                </Field>
                <Field label="Status">
                  <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as AssuranceStatus })}>
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="in-review">In review</option>
                    <option value="accepted">Accepted</option>
                    <option value="closed">Closed</option>
                  </select>
                </Field>
                <Field label="Linked design risks" wide>
                  <div className="check-grid">
                    {risks.map((risk) => (
                      <label key={risk.id}>
                        <input
                          type="checkbox"
                          checked={draft.linkedRiskIds.includes(risk.id)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              linkedRiskIds: e.target.checked
                                ? [...draft.linkedRiskIds, risk.id]
                                : draft.linkedRiskIds.filter((id) => id !== risk.id),
                            })
                          }
                        />
                        <span>{risk.designElement || risk.hazard || "Untitled risk"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Linked critical controls" wide>
                  <div className="check-grid">
                    {controls.map((control) => (
                      <label key={control.id}>
                        <input
                          type="checkbox"
                          checked={draft.linkedControlIds.includes(control.id)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              linkedControlIds: e.target.checked
                                ? [...draft.linkedControlIds, control.id]
                                : draft.linkedControlIds.filter((id) => id !== control.id),
                            })
                          }
                        />
                        <span>{control.title || "Untitled control"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {draft.title && draft.description && draft.lessons ? (
                  <><CheckCircle2 size={17} /> Actionable learning captured</>
                ) : (
                  <><FileWarning size={17} /> Title, event and lesson are required</>
                )}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button className="button button-primary" disabled={!draft.title.trim() || !draft.description.trim() || !draft.lessons.trim()} onClick={save}>
                Save learning record
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
